import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { bottomWithSafeArea } from '../constants/safeArea';
import { paperTextureModal } from './paperTexture';

interface MobileContextToolbarProps {
  children?: React.ReactNode;
  confirmAction?: React.ReactNode;
  hasChanged?: boolean;
  hideWhenIdle?: boolean;
}

interface ContentWidths {
  actions: number;
  confirm: number;
}

const useVisualViewportBottomOffset = (): number => {
  const [bottomOffset, setBottomOffset] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const updateBottomOffset = () => {
      setBottomOffset(Math.max(
        0,
        Math.round(window.innerHeight - viewport.height - viewport.offsetTop),
      ));
    };

    updateBottomOffset();
    viewport.addEventListener('resize', updateBottomOffset);
    viewport.addEventListener('scroll', updateBottomOffset);
    return () => {
      viewport.removeEventListener('resize', updateBottomOffset);
      viewport.removeEventListener('scroll', updateBottomOffset);
    };
  }, []);

  return bottomOffset;
};

const MobileContextToolbar: React.FC<MobileContextToolbarProps> = ({
  children,
  confirmAction,
  hasChanged = false,
  hideWhenIdle = false,
}) => {
  const actionsRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const [widths, setWidths] = useState<ContentWidths>({ actions: 0, confirm: 0 });
  const visualViewportBottomOffset = useVisualViewportBottomOffset();
  const actions = React.Children.toArray(children);

  const measure = useCallback(() => {
    const actions = Math.ceil(actionsRef.current?.getBoundingClientRect().width ?? 0);
    const confirm = Math.ceil(confirmRef.current?.getBoundingClientRect().width ?? 0);
    setWidths((current) => (
      current.actions === actions && current.confirm === confirm
        ? current
        : { actions, confirm }
    ));
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [children, confirmAction, measure]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    if (actionsRef.current) observer.observe(actionsRef.current);
    if (confirmRef.current) observer.observe(confirmRef.current);
    return () => observer.disconnect();
  }, [measure]);

  const confirmWidth = confirmAction && hasChanged ? widths.confirm : 0;
  const isConfirmVisible = Boolean(confirmAction && hasChanged);
  const isSurfaceVisible = !hideWhenIdle
    || actions.length > 0
    || isConfirmVisible;
  const measuredWidth = widths.actions + confirmWidth;

  return (
    <Surface
      $contentWidth={measuredWidth}
      $visible={isSurfaceVisible}
      $visualViewportBottomOffset={visualViewportBottomOffset}
      data-has-changed={hasChanged ? 'true' : 'false'}
      data-toolbar-visible={isSurfaceVisible ? 'true' : 'false'}
    >
      <ActionsLayer
        $visible={isSurfaceVisible}
        role="toolbar"
        aria-label="Page actions"
        aria-hidden={!isSurfaceVisible}
      >
        <ConfirmSlot
          $contentWidth={widths.confirm}
          $visible={isConfirmVisible}
          aria-hidden={!isConfirmVisible}
          data-confirm-visible={isConfirmVisible ? 'true' : 'false'}
        >
          <ConfirmContent ref={confirmRef} $visible={isConfirmVisible}>
            {confirmAction}
            {actions.length > 0 && (
              <ActionDivider aria-hidden="true" data-action-divider />
            )}
          </ConfirmContent>
        </ConfirmSlot>
        <ActionItems ref={actionsRef}>
          {actions.map((action, index) => (
            <React.Fragment key={React.isValidElement(action) ? action.key ?? index : index}>
              {index > 0 && <ActionDivider aria-hidden="true" data-action-divider />}
              {action}
            </React.Fragment>
          ))}
        </ActionItems>
      </ActionsLayer>
    </Surface>
  );
};

const Surface = styled.div<{
  $contentWidth: number;
  $visible: boolean;
  $visualViewportBottomOffset: number;
}>`
  position: fixed;
  left: 50%;
  bottom: ${({ $visualViewportBottomOffset }) => `calc(
    ${bottomWithSafeArea('clamp(30px, 7vw, 40px)')} + ${$visualViewportBottomOffset}px
  )`};
  transform: translateX(-50%);
  isolation: isolate;
  z-index: 4; /* 섬·페이지 크롬과 같은 단. 메뉴 배경(5)·햄버거(6)·모달(10) 아래 */
  width: ${({ $contentWidth, $visible }) => $visible
    ? `min(
      ${$contentWidth > 0 ? $contentWidth + 4 : 40}px,
      calc(100vw - 24px)
    )`
    : '0'};
  height: 30px;
  padding: 2px;
  box-sizing: border-box;
  overflow: hidden;
  ${paperTextureModal}
  border-radius: 1px;
  border: none;
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
  pointer-events: ${({ $visible }) => $visible ? 'auto' : 'none'};
  transition:
    width 460ms ease-in-out,
    opacity 220ms ease ${({ $visible }) => $visible ? '0ms' : '240ms'},
    visibility ${({ $visible }) => $visible ? '0s' : '0s 460ms'};
  will-change: width;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ContentLayer = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 2px;
  width: max-content;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
  pointer-events: ${({ $visible }) => $visible ? 'auto' : 'none'};
  transition:
    opacity 220ms ease ${({ $visible }) => $visible ? '100ms' : '0ms'},
    visibility ${({ $visible }) => $visible ? '0s' : '0s 220ms'};
  white-space: nowrap;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ActionsLayer = styled(ContentLayer)`
  right: 2px;
  gap: 0;
`;

const ActionItems = styled.div`
  width: max-content;
  height: 26px;
  display: flex;
  align-items: center;
  gap: 0;
`;

const ConfirmSlot = styled.div<{ $contentWidth: number; $visible: boolean }>`
  position: relative;
  flex: 0 0 auto;
  width: ${({ $contentWidth, $visible }) => $visible
    ? `${$contentWidth}px`
    : '0'};
  height: 26px;
  overflow: hidden;
  visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
  pointer-events: ${({ $visible }) => $visible ? 'auto' : 'none'};
  transition:
    width 460ms ease-in-out,
    visibility ${({ $visible }) => $visible ? '0s' : '0s 220ms'};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ConfirmContent = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: max-content;
  height: 26px;
  display: flex;
  align-items: center;
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: opacity 220ms ease ${({ $visible }) => $visible ? '100ms' : '0ms'};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ActionDivider = styled.span`
  flex: 0 0 auto;
  width: 1px;
  height: 12px;
  margin: 2px;
  background-color: ${({ theme }) => theme.colors.color_Gray_05};
`;

export default MobileContextToolbar;
